'use client';

import {
  ButtonV3,
  LayoutGrid,
  TypographyV3,
  useHeaderContext,
} from '@jouzen/ecom-components';
import { cx } from 'class-variance-authority';
import { useTranslations } from 'next-intl';
import type { ComponentType, ReactNode } from 'react';
import { useEffect } from 'react';
import type { MessageKey } from 'types/LocaleMessageKey';

import ImagesSlideOverOnScroll from '@/app/[locale]/about-us/_components/ImagesSlideOverOnScroll';
import ScrollTeaser from '@/app/[locale]/about-us/_components/ScrollTeaser';
import Image from '@/app/components/Image';
import LandingPageHero from '@/app/components/LegacyLandingPage/LandingPageHero';
import SvgIconOutlineAltitude from '@/app/icons/IconOutlineAltitude';
import SvgIconOutlineCheck from '@/app/icons/IconOutlineCheck';
import SvgIconOutlineEye from '@/app/icons/IconOutlineEye';
import SvgIconOutlineHuman from '@/app/icons/IconOutlineHuman';
import SvgIconOutlineWe from '@/app/icons/IconOutlineWe';

import SLIDE_IMAGES from './data/slide-images';
import { STAT_LINES } from './data/stats';

interface ItalicHeadingProps {
  readonly messageChunks: ReactNode;
}

interface StatLineProps {
  readonly figure: MessageKey;
  readonly caption: MessageKey;
}

const ourValues: [MessageKey, MessageKey, ComponentType][] = [
  ['about_us_not_me', 'about_us_not_me_copy', SvgIconOutlineWe],
  ['about_us_human_first', 'about_us_human_first_copy', SvgIconOutlineHuman],
  [
    'about_us_trust_matters',
    'about_us_trust_matters_copy',
    SvgIconOutlineCheck,
  ],
  ['about_us_aim_higher', 'about_us_aim_higher_copy', SvgIconOutlineAltitude],
  [
    'about_us_we_find_solutions',
    'about_us_we_find_solutions_copy',
    SvgIconOutlineEye,
  ],
];

const ItalicHeading = ({ messageChunks }: ItalicHeadingProps): JSX.Element => (
  <em className="block pl-20 font-serif italic md:pl-40">{messageChunks}</em>
);

const StatLine = ({ figure, caption }: StatLineProps) => {
  const t = useTranslations();
  return (
    <div
      className={cx(
        'gap-4 md:flex md:items-center md:justify-between',
        'col-main grid md:col-start-4 md:col-end-22',
        'pb-5 md:py-10',
        'border-b border-gray-400',
      )}
    >
      <TypographyV3
        variant="h1"
        Element="strong"
        font="serif"
        className="pr-4 font-extralight tracking-tight"
      >
        {t(figure)}
      </TypographyV3>
      <TypographyV3
        Element="span"
        variant="h6"
        className="text-sandstone-500 md:text-right"
      >
        {t(caption)}
      </TypographyV3>
    </div>
  );
};

const Content = (): JSX.Element => {
  const t = useTranslations();
  const { setInverse } = useHeaderContext()!;

  useEffect(() => {
    setInverse(false);
    return () => setInverse(false);
  }, [setInverse]);

  return (
    <>
      <ScrollTeaser content="scroll_to_discover" outline />
      <LandingPageHero
        useFullHeight={false}
        title="about_us_hero_title"
        subtitle="about_us_hero_copy"
        imgSrc="about-us/hero-d-img2x"
        imgAlt="reproductive_health_hero_alt"
        className="pt-0 text-sandstone-500 md:pt-0 lg:min-h-0"
        copyClassNames="col-start-2 mt-4"
      />

      <LayoutGrid>
        <div className="col-main mb-20 mt-12 md:col-start-7 md:col-end-17 md:mt-0 lg:col-end-21">
          <TypographyV3 Element="p" variant="h6">
            {t('about_us_secondary_copy')}
          </TypographyV3>
        </div>
      </LayoutGrid>
      <ImagesSlideOverOnScroll
        containerStyles="-my-32 pt-6 md:pt-3 md:-my-[7.5rem] lg:pt-0 lg:-my-32"
        listStyles="px-[80vw]"
        wrapperStyles="md:pt-25 pb-40 md:pb-48"
        Heading={
          <TypographyV3
            Element="h2"
            variant="h1"
            className={cx(
              'mb-20 md:mb-0',
              'col-main',
              'md:col-start-4 md:col-end-22',
              'z-10', // so it sits on top of the images list, below
            )}
          >
            {t.rich('about_us_secondary_title', {
              i(chunks) {
                return <ItalicHeading messageChunks={chunks} />;
              },
            })}
          </TypographyV3>
        }
        images={SLIDE_IMAGES}
      />
      <LayoutGrid className="md:pt-24">
        {STAT_LINES.map((stat) => (
          <StatLine key={stat.id} figure={stat.figure} caption={stat.caption} />
        ))}
        <div className="col-main mb-4 mt-8 md:col-start-4 md:col-end-12 md:my-0 md:pt-8">
          <Image
            src="about-us/about-us-data"
            width={566}
            height={348}
            alt={t('about_us_medical_alt')}
          />
          <TypographyV3 Element="h4" variant="h4" className="mb-3 mt-6">
            {t('about_us_data_title')}
          </TypographyV3>
          <TypographyV3 Element="p" variant="base">
            {t('about_us_data_copy')}
          </TypographyV3>
        </div>
        <div className="col-main md:col-start-14 md:col-end-22 md:mt-56 md:pt-8">
          <Image
            src="about-us/about-us-medical"
            width={566}
            height={348}
            alt={t('about_us_medical_alt')}
          />
          <TypographyV3 Element="h4" variant="h4" className="mb-3 mt-6">
            {t('about_us_medical_title')}
          </TypographyV3>
          <TypographyV3 Element="p" variant="base">
            {t('about_us_medical_copy')}
          </TypographyV3>
          <ButtonV3
            variant="secondary-dark"
            className="mt-10"
            href="/medical-advisory-board"
          >
            {t('learn_more')}
          </ButtonV3>
        </div>
      </LayoutGrid>
      <div className="mt-16 border-b border-gray-100 bg-gray-100 py-24">
        <LayoutGrid>
          <TypographyV3
            variant="h3"
            Element="h2"
            className="col-main lg:col-start-4 lg:col-end-9"
          >
            {t('our_values')}
          </TypographyV3>
          {ourValues.map(([title, copy, Icon]) => (
            <div
              key={title}
              className="col-main mb-8 odd:lg:col-start-16 odd:lg:col-end-22 even:lg:col-start-10 even:lg:col-end-15"
            >
              <Icon />
              <TypographyV3 variant="h6" className="py-3">
                {t(title)}
              </TypographyV3>
              <TypographyV3>{t(copy)}</TypographyV3>
            </div>
          ))}
        </LayoutGrid>
      </div>
    </>
  );
};

export default Content;
